import { useCallback, useMemo, useRef, useState } from 'react';

import { OutreachWarningPill } from '@apps/web/components/outreaches/outreach_warning_message';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Spinner } from '@apps/web/components/ui/spinner';
import { comparePersonNames } from '@apps/web/lib/caregiver_name';
import {
    type CaregiverShiftIds,
    expandCaregiverShiftIdsToWholeGroups,
    getPartialGroupSelectionWarnings,
    type PartialGroupSelectionWarning,
} from '@apps/web/lib/caregiver_shift_selections';

type ShiftForWarning = {
    readonly id: string;
    readonly shift_start_time: string;
};

type ShiftGroupForWarning = {
    readonly id?: string;
    readonly shiftIds?: ReadonlyArray<string>;
    readonly shift_ids?: ReadonlyArray<string>;
    readonly groupMode?: 'all' | 'any';
    readonly group_mode?: 'all' | 'any';
};

type PendingWarning = {
    readonly caregiverNameById: Readonly<Record<string, string>>;
    readonly caregiverShiftIds: CaregiverShiftIds;
    readonly warnings: Array<PartialGroupSelectionWarning>;
    readonly onConfirm: (caregiverShiftIds: CaregiverShiftIds) => Promise<void>;
};

type WarningSummary = {
    readonly groupIndex: number;
    readonly caregivers: Array<{ readonly id: string; readonly label: string }>;
    readonly missingShifts: Array<{
        readonly id: string;
        readonly label: string;
    }>;
};

export function useGroupSelectionWarning({
    shifts,
    shiftGroups,
    shiftLabelById,
    sortByLastName = false,
}: {
    readonly shifts: ReadonlyArray<ShiftForWarning>;
    readonly shiftGroups: ReadonlyArray<ShiftGroupForWarning>;
    readonly shiftLabelById: Map<string, string>;
    readonly sortByLastName?: boolean;
}) {
    const [pending, setPending] = useState<PendingWarning | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Use ref to avoid stale closure in onConfirm callbacks
    const shiftsRef = useRef(shifts);
    shiftsRef.current = shifts;
    const shiftGroupsRef = useRef(shiftGroups);
    shiftGroupsRef.current = shiftGroups;

    const checkForWarnings = useCallback(
        ({
            caregiverShiftIds,
            caregiverNamesById,
            onConfirm,
        }: {
            readonly caregiverShiftIds: CaregiverShiftIds;
            readonly caregiverNamesById?: Readonly<Record<string, string>>;
            readonly onConfirm: (
                caregiverShiftIds: CaregiverShiftIds,
            ) => Promise<void>;
        }): boolean => {
            if (shiftGroups.length === 0) {
                return false;
            }

            const warnings = getPartialGroupSelectionWarnings({
                caregiverShiftIds,
                shifts,
                shiftGroups,
            });
            if (warnings.length === 0) {
                return false;
            }

            setPending({
                caregiverNameById: caregiverNamesById ?? {},
                caregiverShiftIds,
                warnings,
                onConfirm,
            });
            return true;
        },
        [shifts, shiftGroups],
    );

    const handleConfirm = useCallback(async () => {
        if (!pending) {
            return;
        }

        const caregiverShiftIds = expandCaregiverShiftIdsToWholeGroups({
            caregiverShiftIds: pending.caregiverShiftIds,
            shifts: shiftsRef.current,
            shiftGroups: shiftGroupsRef.current,
        });

        setIsSubmitting(true);
        try {
            await pending.onConfirm(caregiverShiftIds);
            setPending(null);
        } finally {
            setIsSubmitting(false);
        }
    }, [pending]);

    const handleDismiss = useCallback(() => {
        if (!isSubmitting) {
            setPending(null);
        }
    }, [isSubmitting]);

    const summaries = useMemo((): Array<WarningSummary> => {
        if (!pending) {
            return [];
        }

        const byGroupIndex = new Map<
            number,
            {
                caregiverIds: Set<string>;
                missingShiftIds: Set<string>;
            }
        >();

        for (const warning of pending.warnings) {
            const existing = byGroupIndex.get(warning.groupIndex) ?? {
                caregiverIds: new Set<string>(),
                missingShiftIds: new Set<string>(),
            };
            existing.caregiverIds.add(warning.caregiverId);
            for (const shiftId of warning.missingShiftIds) {
                existing.missingShiftIds.add(shiftId);
            }
            byGroupIndex.set(warning.groupIndex, existing);
        }

        return Array.from(byGroupIndex.entries())
            .sort(([a], [b]) => a - b)
            .map(([groupIndex, summary]) => ({
                groupIndex,
                caregivers: Array.from(summary.caregiverIds)
                    .map((id) => ({
                        id,
                        label: pending.caregiverNameById[id] ?? 'Caregiver',
                    }))
                    .sort((a, b) =>
                        comparePersonNames(a.label, b.label, sortByLastName),
                    ),
                missingShifts: Array.from(summary.missingShiftIds).map(
                    (id) => ({
                        id,
                        label: shiftLabelById.get(id) ?? 'Shift details',
                    }),
                ),
            }));
    }, [pending, shiftLabelById, sortByLastName]);

    return {
        checkForWarnings,
        warningDialogProps: {
            open: pending !== null,
            onDismiss: handleDismiss,
            onConfirm: handleConfirm,
            summaries,
            isSubmitting,
        },
    };
}

type GroupSelectionWarningDialogProps = {
    readonly open: boolean;
    readonly onDismiss: () => void;
    readonly onConfirm: () => Promise<void>;
    readonly summaries: Array<WarningSummary>;
    readonly isSubmitting: boolean;
    readonly confirmLabel?: string;
};

export function GroupSelectionWarningDialog({
    open,
    onDismiss,
    onConfirm,
    summaries,
    isSubmitting,
    confirmLabel = 'Start Outreach',
}: GroupSelectionWarningDialogProps) {
    return (
        <Dialog
            open={open}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    onDismiss();
                }
            }}
        >
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>
                        Some shifts must go to the same caregiver
                    </DialogTitle>
                    <DialogDescription>
                        These caregivers are only on part of a group. Add them
                        to the rest of it to continue.
                    </DialogDescription>
                </DialogHeader>
                {summaries.length > 0 && (
                    <div className="max-h-[50vh] space-y-3 overflow-y-auto">
                        {summaries.map(
                            ({ groupIndex, caregivers, missingShifts }) => (
                                <div
                                    key={groupIndex}
                                    className="rounded-lg border p-3"
                                >
                                    <div className="text-sm font-medium">
                                        Group {groupIndex + 1}
                                    </div>
                                    <div className="text-muted-foreground mt-1 text-sm">
                                        Only on part of this group:{' '}
                                        <span className="inline-flex flex-wrap gap-1">
                                            {caregivers.map((caregiver) => (
                                                <OutreachWarningPill
                                                    key={caregiver.id}
                                                    entity={{
                                                        ...caregiver,
                                                        type: 'caregiver',
                                                    }}
                                                />
                                            ))}
                                        </span>
                                    </div>
                                    <div className="text-muted-foreground mt-2 text-sm">
                                        Not yet included:
                                    </div>
                                    <ul className="mt-1 list-disc space-y-1 pl-5 text-sm">
                                        {missingShifts.map((shift) => (
                                            <li key={shift.id}>
                                                <OutreachWarningPill
                                                    entity={{
                                                        ...shift,
                                                        type: 'shift',
                                                    }}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ),
                        )}
                    </div>
                )}
                <p className="text-muted-foreground rounded-lg border p-3 text-sm">
                    Continuing also asks them about the shifts listed above.
                </p>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onDismiss}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button onClick={onConfirm} disabled={isSubmitting}>
                        {isSubmitting ? <Spinner className="mr-2" /> : null}
                        {confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
